<template>
  <SideModal
    location="left"
    width="500px"
    :show="show && !!eventDay"
    @update:show="$emit('update:show', $event)"
    close-on-overlay-click
  >
    <div class="modal-top py-1">
      <span class="modal-title d-flex align-center gap-3">
        <span>
          {{ dayjs(eventDay.start).format("dddd, MMMM D, YYYY") }}
        </span>
        <v-icon size="13px" color="white" @click="copyToClipboard(eventDay.conference_days_id)"
          >mdi-content-copy</v-icon
        >
      </span>
      <v-btn
        @click="$emit('update:show', false)"
        icon
        size="large"
        data-cy="event-day-options-modal-close-btn"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <div class="modal-body">
      <div class="title">Options</div>
      <div class="item-group justify-space-between px-4">
        <div class="item">
          <div class="title">
            Travel Time
            <v-tooltip location="bottom" activator="parent">
              Number of minutes the parent must have in between meeting.
              <br />
              (Impacts parents only. Not teachers)
            </v-tooltip>
          </div>
          <v-text-field
            type="number"
            style="width: 80px"
            color="primary"
            density="compact"
            class="custom-input"
            :model-value="eventDay.travel_time"
            @update:model-value="
              if ((+$event || +$event === 0) && +$event >= 0)
                $emit('update:eventDay', { travel_time: +$event });
            "
            data-cy="event-day-option-travel-time"
          ></v-text-field>
        </div>
        <div class="item">
          <div class="title">
            Passing Time
            <v-tooltip location="bottom" activator="parent">
              Number of minutes between each meeting.
              <br />
              (Impacts teachers and parents)
            </v-tooltip>
          </div>
          <v-text-field
            type="number"
            style="width: 80px"
            color="primary"
            density="compact"
            class="custom-input"
            :model-value="eventDay.passing_time"
            @update:model-value="
              if ((+$event || +$event === 0) && +$event >= 0)
                $emit('update:eventDay', { passing_time: +$event });
            "
            data-cy="event-day-option-passing-time"
          ></v-text-field>
        </div>
      </div>
      <div class="item-group px-4">
        <div class="item">
          <v-checkbox
            :model-value="eventDay.allow_overbooking"
            @update:model-value="
              $emit('update:eventDay', {
                allow_overbooking: $event
              })
            "
            density="compact"
            class="custom-input"
            label="Allow Overbooking"
            data-cy="event-day-option-overbooking"
          ></v-checkbox>
          <v-tooltip activator="parent" location="bottom" class="custom-tooltip">
            Allow more than one person to book the same appointment on a schedule.<br />
            You can set the number of seats in the schedules page.
          </v-tooltip>
        </div>
      </div>
      <div class="title mt-4">Meeting Types</div>

      <div class="meeting-types-con mt-4">
        <div
          data-cy="event-day-option-meeting-type"
          data-meeting-type="in_person"
          @click="
            $emit('update:eventDay', {
              meeting_types: {
                ...(eventDay.meeting_types || {}),
                in_person: !eventDay.meeting_types?.in_person
              }
            })
          "
          :class="['meeting-type', { enabled: eventDay.meeting_types?.in_person }]"
        >
          In Person
        </div>
        <div
          data-cy="event-day-option-meeting-type"
          data-meeting-type="virtual"
          @click="
            $emit('update:eventDay', {
              meeting_types: {
                ...(eventDay.meeting_types || {}),
                virtual: !eventDay.meeting_types?.virtual
              }
            })
          "
          :class="['meeting-type', { enabled: eventDay.meeting_types?.virtual }]"
        >
          Virtual
        </div>
        <div
          data-cy="event-day-option-meeting-type"
          data-meeting-type="phone"
          @click="
            $emit('update:eventDay', {
              meeting_types: {
                ...(eventDay.meeting_types || {}),
                phone: !eventDay.meeting_types?.phone
              }
            })
          "
          :class="['meeting-type', { enabled: eventDay.meeting_types?.phone }]"
        >
          Phone
        </div>
        <div
          data-cy="event-day-option-meeting-type"
          data-meeting-type="callback"
          @click="
            $emit('update:eventDay', {
              meeting_types: {
                ...(eventDay.meeting_types || {}),
                callback: !eventDay.meeting_types?.callback
              }
            })
          "
          :class="['meeting-type', { enabled: eventDay.meeting_types?.callback }]"
        >
          Callback
        </div>
      </div>
      <div class="item mt-4">
        <v-btn
          @click="saveOptions"
          :loading="state.loadingOptions"
          style="text-transform: capitalize; width: 100px"
          data-cy="event-day-options-save-btn"
          >Save</v-btn
        >
      </div>
    </div>
  </SideModal>
</template>

<script setup lang="ts">
import type { ConferenceDay } from "@/api_types/teacher";
import SideModal from "@/components/sideModal.vue";
import { useConferenceStore } from "@/stores/teacher/conference";
import { useAlertsStore } from "@/stores/alerts";

import { copyToClipboard } from "@/utils";
import dayjs from "dayjs";
import { reactive } from "vue";

const props = defineProps<{
  show: boolean;
  eventDay: ConferenceDay;
}>();

const state = reactive({ search: "", on: true, loadingOptions: false, loadingParents: false });

defineEmits<{
  (event: "update:show", val: boolean): void;
  (event: "update:eventDay", val: Partial<ConferenceDay>);
}>();

const confStore = useConferenceStore();
const alertStore = useAlertsStore();

const saveOptions = async () => {
  state.loadingOptions = true;
  const res = await confStore.apiUpdateConferenceDay(props.eventDay.conference_days_id, {
    travel_time: props.eventDay.travel_time,
    passing_time: props.eventDay.passing_time,
    meeting_types: {
      callback: props.eventDay.meeting_types?.callback,
      in_person: props.eventDay.meeting_types?.in_person,
      phone: props.eventDay.meeting_types?.phone,
      virtual: props.eventDay.meeting_types?.virtual,
      virtual_type: props.eventDay.meeting_types?.virtual_type || "url"
    },
    allow_overbooking: props.eventDay.allow_overbooking
  });
  if (res?.status){
    if (!(res?.data.conference_days_id && 
        confStore.updatedConferenceDay(res.data.conference_id, res.data.conference_days_id, {
          travel_time: res.data.travel_time,
          passing_time: res.data.passing_time,
          meeting_types: res.data.meeting_types,
          allow_overbooking: res.data.allow_overbooking
        })
      )
    ) {
      confStore.load();
    }
    alertStore.addAlert("success","Event Day Settings Saved");
  }else{
    alertStore.addAlert("error", res.message);
  }
  state.loadingOptions = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.meeting-types-con {
  display: flex;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  .meeting-type {
    width: 48%;
    margin-bottom: 4%;
    padding: 10px 0;
    border-radius: 10px;
    text-align: center;
    background-color: $gray-100;
    border: 1px solid $gray-300;
    font-weight: 600;
    cursor: pointer;
    font-size: 1.1rem;

    &.enabled {
      background-color: $secondary;
      color: white;
    }
  }
}

.modal-body {
  > .title {
    color: $primary;
    font-size: 1.1rem;
    font-weight: 700;
    // padding: 5px 0 25px;
    // margin-top: 10px;
  }

  .item-group {
    display: flex;
    width: 100%;
    gap: 30px;
    flex-wrap: wrap;
  }
  .item {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 10px;
    .title {
      color: rgb(100, 100, 100);
      font-weight: 600;
    }
  }
}
.modal-top {
  display: flex;
  align-items: center;
  background-color: $primary;
  min-width: 100%;
  justify-content: space-between;
  .modal-title {
    font-weight: 600;
    font-size: 1.15rem;
    margin: auto 0 auto 20px;
    color: white;
  }
}
.small-col {
  width: min-content;
}
</style>
